import react, { useState, useEffect } from 'react';
import {
  request,
  setAuthHeader,
} from '../../api/authenticationService';
import { useNavigate } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';

const LoginPage = ({ loading, error, setData, ...props }) => {
  const [values, setValues] = useState({
    userName: '',
    password: '',
    twoFaCodeFromUser: '',
  });

  const [rememberUsername, setRememberUsername] = useState(false);
  const [loginFailure, setLoginFailure] = useState('');
  const [otp, setOtp] = useState(false);
  const [isLoginLoading, setIsLoginLoading] = useState(false); // State to track loading

  const [showPassword, setShowPassword] = useState(false);

  //  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const savedUserName = localStorage.getItem('savedUserName');
    if (savedUserName) {
      setValues((prev) => ({
        ...prev,
        userName: savedUserName,
      }));
      setRememberUsername(true);
    }
  }, []);

  const onLoginOTP = (e) => {
    e.preventDefault();
    const { userName, twoFaCodeFromUser } = values;
    if (userName.trim() === '' || twoFaCodeFromUser.trim() === '')
      return;

    if (rememberUsername) {
      localStorage.setItem('savedUserName', userName);
    } else {
      localStorage.removeItem('savedUserName');
    }

    const doLoginOTP = async () => {
      setIsLoginLoading(true); // Disable the button
      try {
        const response = await request('POST', '/auth/verify-otp', {
          userName: userName,
          twoFaCodeFromUser: twoFaCodeFromUser,
        });
        setAuthHeader(response.data.token);
        setLoginFailure('');

        const response2 = await request('GET', '/auth/userinfo', {});
        setData(response2.data);

        response2?.data?.roles?.filter(
          (value) => value.roleCode === 'SUPER_ADMIN',
        ).length > 0
          ? navigate('/schoolsuperadmin')
          : navigate('/school');

        setOtp(false);
        setValues((prev) => ({
          ...prev,
          password: '',
          twoFaCodeFromUser: '',
        }));
      } catch (err) {
        console.log(err);
        localStorage.clear();
        setAuthHeader(null);
        console.log('ERROR::..');
        setTimeout(() => {
          setLoginFailure('');
        }, 5000);
        if (err?.response) {
          setOtp(false);
          setLoginFailure('Something Wrong! Please Try Again');
        }
      } finally {
        setIsLoginLoading(false); // Enable the button
      }
    };
    doLoginOTP();
  };

  const onLogin = (e) => {
    e.preventDefault();
    const { userName, password } = values;
    if (userName.trim() === '' || password.trim() === '') return;

    if (rememberUsername) {
      localStorage.setItem('savedUserName', userName);
    } else {
      localStorage.removeItem('savedUserName');
    }

    const doLogin = async () => {
      setIsLoginLoading(true); // Disable the button
      try {
        const response = await request('POST', '/auth/login', {
          userName: userName,
          password: password,
        });
        if (response.status === 204) {
          console.log('204 status');
          setLoginFailure('OTP required!');
          setOtp(true);
          return;
        }
        setAuthHeader(response.data.token);
        setLoginFailure('');

        const response2 = await request('GET', '/auth/userinfo', {});
        setData(response2.data);

        response2?.data?.roles?.filter(
          (value) => value.roleCode === 'SUPER_ADMIN',
        ).length > 0
          ? navigate('/schoolsuperadmin')
          : response2?.data?.roles?.filter(
                (value) => value.roleCode === 'ADMIN',
              ).length > 0
            ? navigate('/schooladmin')
            : navigate('/school');

        setValues((prev) => ({
          ...prev,
          password: '',
          twoFaCodeFromUser: '',
        }));
      } catch (err) {
        console.log(err);
        localStorage.clear();
        setAuthHeader(null);
        console.log('ERROR::..');
        setTimeout(() => {
          setLoginFailure('');
        }, 5000);
        if (err?.response) {
          switch (err.response.status) {
            case 401:
              console.log('401 status');
              setLoginFailure(
                'Authentication Failed. Bad Credentials',
              );
              break;

            default:
              setLoginFailure('Something Wrong! Please Try Again');
          }
        } else {
          setLoginFailure('Something Wrong! Please Try Again');
        }
      } finally {
        setIsLoginLoading(false); // Enable the button
      }
    };
    doLogin();
  };

  const handleSubmit = (evt) => {
    evt.preventDefault();
    const { userName, password } = values;
    if (userName.trim() === '' || password.trim() === '') return;
    otp ? onLoginOTP(evt) : onLogin(evt);
  };

  const handleChange = (e) => {
    e.persist();
    setValues((values) => ({
      ...values,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6 py-8 bg-gradient-to-b from-blue-50 to-white">
      <h1 className="flex flex-col items-center text-center mb-8">
        <span className="text-4xl mb-2">👋</span>
        <span className="text-2xl font-semibold text-gray-900">
          WELCOME TO ANNOUR MANAGEMENT SYSTEM
        </span>
      </h1>
      <form onSubmit={handleSubmit}>
        <div className="flex w-72 flex-col gap-6 my-4">
          <div className="relative h-10 w-full min-w-[200px]">
            <input
              data-testid="username"
              name="userName"
              value={values.userName}
              onChange={handleChange}
              className="peer h-full w-full rounded-[7px] border border-blue-gray-200 border-t-transparent bg-white px-3 py-2.5 font-sans text-sm font-semibold text-blue-gray-700 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2 focus:border-purple-700 focus:border-t-transparent focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50"
              placeholder=" "
            />
            <label className="before:content[' '] after:content[' '] pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none text-[11px] font-semibold leading-tight text-blue-gray-400 transition-all before:pointer-events-none before:mt-[6.5px] before:mr-1 before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:border-t before:border-l before:border-blue-gray-200 before:transition-all after:pointer-events-none after:mt-[6.5px] after:ml-1 after:box-border after:block after:h-1.5 after:w-2.5 after:flex-grow after:rounded-tr-md after:border-t after:border-r after:border-blue-gray-200 after:transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[3.75] peer-placeholder-shown:text-blue-gray-500 peer-placeholder-shown:before:border-transparent peer-placeholder-shown:after:border-transparent peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-pink-500 peer-focus:before:border-t-2 peer-focus:before:border-l-2 peer-focus:before:border-pink-500 peer-focus:after:border-t-2 peer-focus:after:border-r-2 peer-focus:after:border-pink-500 peer-disabled:text-transparent peer-disabled:before:border-transparent peer-disabled:after:border-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500">
              User Name
            </label>
          </div>
        </div>
        {otp ? (
          <div className="flex w-72 flex-col gap-6 my-4">
            <div className="relative h-10 w-full min-w-[200px]">
              <input
                name="twoFaCodeFromUser"
                onChange={handleChange}
                className="peer h-full w-full rounded-[7px] border border-blue-gray-200 border-t-transparent bg-white px-3 py-2.5 font-sans text-sm font-semibold text-blue-gray-700 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2 focus:border-purple-700  focus:border-t-transparent focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50"
                placeholder=" "
                autoComplete="off"
              />
              <label className="before:content[' '] after:content[' '] pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none text-[11px] font-semibold leading-tight text-blue-gray-400 transition-all before:pointer-events-none before:mt-[6.5px] before:mr-1 before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:border-t before:border-l before:border-blue-gray-200 before:transition-all after:pointer-events-none after:mt-[6.5px] after:ml-1 after:box-border after:block after:h-1.5 after:w-2.5 after:flex-grow after:rounded-tr-md after:border-t after:border-r after:border-blue-gray-200 after:transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[3.75] peer-placeholder-shown:text-blue-gray-500 peer-placeholder-shown:before:border-transparent peer-placeholder-shown:after:border-transparent peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-pink-500 peer-focus:before:border-t-2 peer-focus:before:border-l-2 peer-focus:before:border-pink-500 peer-focus:after:border-t-2 peer-focus:after:border-r-2 peer-focus:after:border-pink-500 peer-disabled:text-transparent peer-disabled:before:border-transparent peer-disabled:after:border-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500">
                One Time Pad
              </label>
            </div>
          </div>
        ) : (
          <div className="flex w-72 flex-col gap-6 my-4">
            <div className="relative h-10 w-full min-w-[200px]">
              <input
                name="password"
                data-testid="password"
                value={values.password}
                type={showPassword ? 'text' : 'password'}
                onChange={handleChange}
                className="peer h-full w-full rounded-[7px] border border-blue-gray-200 border-t-transparent bg-white px-3 py-2.5 font-sans text-sm font-semibold text-blue-gray-700 outline outline-0 transition-all placeholder-shown:border placeholder-shown:border-blue-gray-200 placeholder-shown:border-t-blue-gray-200 focus:border-2 focus:border-purple-700  focus:border-t-transparent focus:outline-0 disabled:border-0 disabled:bg-blue-gray-50"
                placeholder=" "
                autoComplete="off"
              />
              <label className="before:content[' '] after:content[' '] pointer-events-none absolute left-0 -top-1.5 flex h-full w-full select-none text-[11px] font-semibold leading-tight text-blue-gray-400 transition-all before:pointer-events-none before:mt-[6.5px] before:mr-1 before:box-border before:block before:h-1.5 before:w-2.5 before:rounded-tl-md before:border-t before:border-l before:border-blue-gray-200 before:transition-all after:pointer-events-none after:mt-[6.5px] after:ml-1 after:box-border after:block after:h-1.5 after:w-2.5 after:flex-grow after:rounded-tr-md after:border-t after:border-r after:border-blue-gray-200 after:transition-all peer-placeholder-shown:text-sm peer-placeholder-shown:leading-[3.75] peer-placeholder-shown:text-blue-gray-500 peer-placeholder-shown:before:border-transparent peer-placeholder-shown:after:border-transparent peer-focus:text-[11px] peer-focus:leading-tight peer-focus:text-pink-500 peer-focus:before:border-t-2 peer-focus:before:border-l-2 peer-focus:before:border-pink-500 peer-focus:after:border-t-2 peer-focus:after:border-r-2 peer-focus:after:border-pink-500 peer-disabled:text-transparent peer-disabled:before:border-transparent peer-disabled:after:border-transparent peer-disabled:peer-placeholder-shown:text-blue-gray-500">
                Password
              </label>
              <span
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-2 cursor-pointer text-xl text-gray-600 px-1 py-2 hover:text-gray-800"
              >
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
          </div>
        )}
        {!otp && (
          <div className="flex items-center justify-center w-full py-2 my-2">
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                id="rememberUsername"
                className="sr-only peer"
                checked={rememberUsername}
                onChange={(e) =>
                  setRememberUsername(e.target.checked)
                }
              />

              {/* Track */}
              <div
                className="
          w-11 h-6
          bg-gray-300
          rounded-full
          peer-checked:bg-blue-500
          peer-focus:ring-2 
          peer-focus:ring-blue-300
          transition-all duration-300
        "
              ></div>

              {/* Knob */}
              <div
                className="
          absolute left-0.5 top-0.5
          w-5 h-5
          bg-white rounded-full
          transition-all duration-300
          peer-checked:translate-x-5
          peer-checked:scale-110
          peer-checked:shadow-md
        "
              ></div>

              <span className="ml-3 text-gray-800 text-sm select-none">
                Remember username
              </span>
            </label>
          </div>
        )}

        <div className="flex w-72 flex-col gap-6">
          <button
            type="submit"
            data-testid="login-btn"
            onClick={handleSubmit}
            className={`rounded text-white px-6 py-2 font-semibold ${
              isLoginLoading
                ? 'bg-gray-500 cursor-not-allowed'
                : 'bg-green-700 hover:bg-green-900'
            }`}
            disabled={isLoginLoading}
          >
            {isLoginLoading ? (
              <div className="flex justify-center items-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                <span>Logging in...</span>
              </div>
            ) : (
              <div className="flex items-center justify-center gap-2">
                <span>🔐 Login</span>
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </div>
            )}
          </button>
        </div>
      </form>

      {loginFailure.length > 0 && (
        <div className="mt-4 p-4 bg-red-100 border-l-4 border-red-500 rounded-r-lg">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-500"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{loginFailure}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
